import { AlertCircle, FileText, FolderOpen, Users } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { OrgProjectOverview } from "@/stores/organizations/repository";
import { cn } from "@/lib/utils";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";

interface BuildingProjectRowsProps {
  projects: OrgProjectOverview[];
  isLoading: boolean;
  /** Projects matched by the page search — visually highlighted. */
  highlightedProjectIds: Set<number>;
  onProjectClick: (project: OrgProjectOverview) => void;
  /** Opens the create-project flow for this building. Absent when the viewer
   *  cannot create projects — the empty state then renders without the link. */
  onAddProject?: () => void;
  colSpan: number;
}

// One count badge (docs / open issues / members) with an accessible label.
const ProjectCountBadge = (props: { icon: typeof FileText; count: number; label: string }) => {
  const { icon: Icon, count, label } = props;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-muted-foreground"
      title={`${count} ${label}`}
      aria-label={`${count} ${label}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {count}
    </span>
  );
};

// A single clickable project line inside the expansion panel. Extracted so the
// panel component stays under the method-length limit.
const ProjectLine = (props: {
  project: OrgProjectOverview;
  isHighlighted: boolean;
  onClick: () => void;
}) => {
  const { project, isHighlighted, onClick } = props;
  const { t } = useLanguage();
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm",
        "transition-transform duration-200 ease-out hover:translate-x-1",
        isHighlighted && "bg-primary/5 ring-1 ring-primary/30"
      )}
    >
      <FolderOpen className="h-4 w-4 shrink-0 text-primary" />
      <span className="min-w-0 flex-1 truncate font-medium">{project.projectName}</span>
      <ProjectStatusBadge status={project.status} />
      {project.isDefault && (
        <Badge variant="secondary" className="shrink-0 text-[10px]">
          {t.buildings.list.defaultProjectBadge}
        </Badge>
      )}
      <span className="flex shrink-0 items-center gap-3">
        <ProjectCountBadge
          icon={FileText}
          count={project.docCount}
          label={t.buildings.list.docsLabel}
        />
        <ProjectCountBadge
          icon={AlertCircle}
          count={project.openIssueCount}
          label={t.buildings.list.issuesLabel}
        />
        <ProjectCountBadge
          icon={Users}
          count={project.memberCount}
          label={t.buildings.list.membersLabel}
        />
      </span>
    </button>
  );
};

/**
 * Expansion panel row under a building row on My Buildings: the building's
 * projects with doc / open-issue / member counts (from get_org_projects_overview).
 * Clicking a project deep-links into the building with that project as the
 * global scope (`?project=`).
 *
 * Shares the expanded building row's ~2% gray tint (the row above drops its
 * bottom divider) so building + projects read as one group; this row's own
 * bottom divider bounds the group below.
 */
export const BuildingProjectRows = (props: BuildingProjectRowsProps) => {
  const { projects, isLoading, highlightedProjectIds, onProjectClick, onAddProject, colSpan } =
    props;
  const { t } = useLanguage();
  return (
    <TableRow className="bg-[#fafafa] hover:bg-[#fafafa]">
      <TableCell colSpan={colSpan} className="px-6 py-2">
        {isLoading ? (
          <Skeleton className="my-1 h-5 w-64" />
        ) : projects.length === 0 ? (
          <p className="py-1 text-sm text-muted-foreground">
            {t.buildings.list.noProjectsInBuilding}
            {onAddProject && (
              <>
                {" "}
                <button
                  type="button"
                  onClick={onAddProject}
                  className="font-medium text-primary hover:underline"
                >
                  {t.buildings.list.addProjectLink}
                </button>
              </>
            )}
          </p>
        ) : (
          // Each line sits in a plain wrapper so the divide-y divider stays put
          // while the button inside translates on hover. Dividers are fainter
          // than the table's row dividers — inside the group only.
          <div className="divide-y divide-[#ececec]">
            {projects.map((project) => (
              <div key={project.projectId}>
                <ProjectLine
                  project={project}
                  isHighlighted={highlightedProjectIds.has(project.projectId)}
                  onClick={() => onProjectClick(project)}
                />
              </div>
            ))}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};
